import type { components } from "#open-fetch-schemas/atlit";
import type { TSeasonType } from "~/common/constants/seasons";
import type { TLeagueSchema } from "~/leagues/schemas/league.schema";
import type { ILeague, ITenantOrganization, TGenders } from "~/types";
import { formatSeasonPayload, formatSeasonStringToDate } from "@/common/helpers/format-season";

import { LEAGUE_TYPE_WITH_STATS_TABS } from "../constants/league-types";

export default function formatLeague(league: components["schemas"]["League"]): ILeague {
  const user = useUser();
  const appEnv = useRuntimeConfig().public.appEnv;

  const canViewStats = league.meta?.permissions?.includes("stats:overview") || false;

  const isLeaderboardsEnabled = () => {
    // TODO: Remove those checks ones we will have proper feature management setup that will support such conditions
    if (appEnv === "production") {
      // https://pixellot.atlassian.net/browse/VSN-5982
      const fpfLeagues = [
        "651abc1ffe6d46e3d10c127e",
        "651abadbd45a63cc69d5e622",
        "651ab9b633a70e80dd196fff",
        "6519652adde57f1d1ada1710",
        "65154a71240fdbe558e4c079",
      ];

      if (fpfLeagues.includes(league.id)) {
        return !!user.value?.hasSuperAdminRole;
      }
    }

    if (!isFeatureEnabled("enable_league_leaderboards_tab")) {
      return false;
    }

    if (!league.attributes.type) {
      return false;
    }
    if (!canViewStats) {
      return false;
    }

    return LEAGUE_TYPE_WITH_STATS_TABS.includes(league.attributes.type);
  };

  const isStandingsEnabled = () => {
    if (!isFeatureEnabled("enable_league_standings_tab")) {
      return false;
    }

    if (!league.attributes.type) {
      return false;
    }

    if (!canViewStats) {
      return false;
    }

    return LEAGUE_TYPE_WITH_STATS_TABS.includes(league.attributes.type);
  };

  const tenant: ITenantOrganization | null = league?.relationships?.tenant
    ? {
        id: league.relationships.tenant.id,
        name: league.relationships.tenant.attributes.name,
        name_short: league.relationships.tenant.attributes.shortName ?? "",
        logo_url: league.relationships.tenant.attributes.logo ?? "",
        location: league.relationships.tenant.attributes.location,
      }
    : null;

  return {
    id: league.id,
    name: league.attributes.name as string,
    name_short: league.attributes.shortName,
    type: league.attributes.type as ILeague["type"], // we need to cast since we are excluding some types (class, division, group)
    logo_url: league.attributes.logo,
    gender: league.attributes.gender as ILeague["gender"],
    level: league.attributes.level,
    sport_type: league.attributes.sportType as ILeague["sport_type"],
    organization_type: league.attributes.organizationType as ILeague["organization_type"],
    fallbackSeason: {
      // @ts-expect-error TODO: Should be added in the `atlit-be` swagger definition
      format: league.attributes.fallbackSeason?.format,
      // @ts-expect-error TODO: Should be added in the `atlit-be` swagger definition
      startDate: formatSeasonStringToDate(league.attributes.fallbackSeason?.startDate),
    },
    season: {
      format: league.attributes.season?.format,
      startDate: formatSeasonStringToDate(league.attributes.season?.startDate),
    },
    location: {
      country: league.attributes.location?.country,
      state: league.attributes.location?.state,
      city: league.attributes.location?.city,
    },
    tenant,
    customAttributes: league.attributes.customAttributes as Record<string, string> | undefined,
    permissions: {
      canRead: league.meta?.permissions?.includes("read") || false,
      canWrite: league.meta?.permissions?.includes("write") || false,
      canDelete: league.meta?.permissions?.includes("delete") || false,
      availableStats: canViewStats,
      availableAthletes: isFeatureEnabled("enable_league_athletes_tab"),
      availableLeaderboards: isLeaderboardsEnabled(),
      availableStandings: isStandingsEnabled(),
    },
  };
}

export function formatLeagueCreatePayload(data: TLeagueSchema): components["schemas"]["CreateLeagueAttr"] {
  const payload: components["schemas"]["CreateLeagueAttr"] = {
    name: data.name,
    shortName: data.name_short,
    type: data.type as ILeague["type"],
    logo: data.logo_file_key || undefined,
    gender: data.gender as TGenders,
    level: data.level || undefined,
    // @ts-expect-error TODO: Sync sport type types
    sportType: data.sport_type,
    organizationType: data.organization_type as components["schemas"]["OrganizationType"],
    season: formatSeasonPayload(data.season) as { format: TSeasonType; startDate?: string },
    tenantId: data.tenantId || undefined,
  };

  if (data.location?.country || data.location?.state || data.location?.city) {
    payload.location = {
      country: data.location?.country || undefined,
      state: data.location?.state || undefined,
      city: data.location?.city || undefined,
    };
  }

  return payload;
}
export function formatLeagueUpdatePayload(data: Partial<TLeagueSchema>): components["schemas"]["UpdateLeagueAttr"] {
  const payload: components["schemas"]["UpdateLeagueAttr"] = {
    name: data.name,
    shortName: data.name_short,
    type: data.type,
    logo: data.logo_file_key || undefined,
    // Non changeable data:
    // gender: data.gender,
    // sportType: data.sport_type,
    // organizationType: data.organization_type
    level: data.level || undefined,
    season: formatSeasonPayload(data.season),
  };

  if (data.location) {
    payload.location = {
      country: data.location?.country,
      state: data.location?.state?.length === 0 ? null : data.location?.state,
      city: data.location?.city?.length === 0 ? null : data.location?.city,
    };
  }

  return payload;
}
